import React, { useState, useEffect } from "react";
import "./HookGenerator.css";
import ClipboardCopyIcon from "../../assets/HookGenerator/copyicon.png";
import { useQuota } from "../../context/QuotaContext";

const topHooks = [
  "I wasn’t going to post this, but…",
  "Here’s what I wish I knew when I started...",
  "Most people won’t tell you this, but...",
  "I landed my dream job without applying. Here’s how.",
  "I failed. And it was the best thing that happened to me.",
  "This one change 10x-ed my productivity.",
  "Your resume doesn’t matter if you do this.",
  "No one talks about this part of leadership...",
  "I asked ChatGPT to write my resume. Here’s what happened.",
  "Don’t make the same mistake I did.",
];

const HookGenerator = () => {
  const [input, setInput] = useState("");
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const { quota, fetchQuota } = useQuota();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const hooksLeft = (() => {
    const limit = quota?.plan_limits?.hooks ?? 0;
    const used = quota?.usage?.hooks ?? 0;
    if (limit === -1) return Infinity;
    return Math.max(limit - used, 0);
  })();

  const generateHooks = async () => {
    if (!input.trim()) return;
    if (hooksLeft <= 0) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/hook-gen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ topic: input, count: 5 }),
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      setHooks(data.hooks.map((h) => h.hook));
      await fetchQuota();
    } catch (error) {
      console.error("Error generating hooks:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="hookgen-wrapper">
      <h1 className="hookgen-title">
        <span className="bold-text">Generate</span>{" "}
        <span className="blue-text">Hooks</span>
      </h1>
      <p className="hookgen-subtitle">
        Just drop your content or describe your topic,
        <br />
        and we’ll create compelling hooks for you
      </p>

      {/* Input Section */}
      <div className="hookgen-input-container">
        <input
          type="text"
          className="hookgen-input"
          placeholder="Describe your topic or paste your content here..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button
          className="hookgen-generate-btn"
          onClick={generateHooks}
          disabled={loading || hooksLeft === 0}
        >
          {loading
            ? "Generating..."
            : hooksLeft === Infinity
            ? "Get Hooks"
            : hooksLeft <= 0
            ? "No Hooks Left"
            : "Get Hooks"}
        </button>
      </div>

      {/* Results Section */}
      {hooks.length > 0 && (
        <section className="hookgen-results">
          <ul className="hookgen-list">
            {hooks.map((hook, index) => (
              <li key={index} className="hookgen-item">
                <span>{hook}</span>
                <button
                  className="hookgen-copy-btn"
                  onClick={() => copyToClipboard(hook, index)}
                >
                  {copiedIndex === index ? "Copied!" : (
                    <img src={ClipboardCopyIcon} alt="Copy" />
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="hookgen-regenerate-container">
            <button
              className="hookgen-regenerate-btn"
              onClick={generateHooks}
              disabled={loading || hooksLeft === 0}
            >
              {loading ? "Regenerating..." : "Regenerate Hooks"}
            </button>
          </div>
        </section>
      )}

      {/* Top Hooks Section */}
      <h2 className="hookgen-top-title">
        Steal Our <span className="blue-text">Top 10</span> LinkedIn Hooks
      </h2>

      <div className="hookgen-top-hooks-grid">
        {topHooks.map((hook, index) => (
          <div className="hookgen-top-card" key={index}>
            <span>{hook}</span>
            <button
              className="copy-btn"
              onClick={() => copyToClipboard(hook, index)}
            >
              {copiedIndex === index ? "Copied!" : (
                <img src={ClipboardCopyIcon} alt="Copy" />
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Info Section */}
      <div className="hookgen-info">
        <h2>
          What is a <span className="blue-text">LinkedIn Hook</span>?
        </h2>
        <p>
          LinkedIn hooks are the attention-grabbing opening lines used at the beginning of a LinkedIn post.
          Their primary purpose is to stop the scroll and compel users to click “see more.”
        </p>
        <p>
          A powerful hook sparks curiosity, creates emotional resonance, or promises value — making it an
          essential part of content strategy for personal branding, business marketing, and thought leadership on LinkedIn.
        </p>
        <p>
          Incorporating strong LinkedIn hooks can significantly boost engagement, increase post visibility, and improve reach within
          your professional network. Whether you're sharing a career story, a marketing tip, or a business insight, an effective LinkedIn
          hook helps your content stand out in a crowded feed.
        </p>
        <p>
          Using well-crafted hooks is a proven strategy for driving more impressions, clicks, and meaningful conversations —
          which are key for LinkedIn SEO and algorithmic performance.
        </p>
      </div>

      {/* Types Section */}
      <div className="hookgen-types">
        <h2>
          Types of <span className="blue-text">LinkedIn Hooks</span>
        </h2>
        <div className="hookgen-types-grid">
          {[
            {
              number: "01",
              title: "Story-Based Hooks",
              desc: "Start with a personal, emotional, or unexpected story to build connection.",
              example: "“Two years ago, I was jobless and confused. Today, I lead a team of 50.”",
            },
            {
              number: "02",
              title: "Curiosity Hooks",
              desc: "Spark intrigue by revealing just enough to make readers want more.",
              example: "“Most people don’t know this, but it completely changed my career.”",
            },
            {
              number: "03",
              title: "Value-Driven Hooks",
              desc: "Promise actionable tips, advice, or insights up front.",
              example: "“Here are 5 free tools that helped me double my productivity.”",
            },
            {
              number: "04",
              title: "Contrarian Hooks",
              desc: "Challenge common beliefs or industry norms to stand out.",
              example: "“Stop networking. Start doing this instead.”",
            },
            {
              number: "05",
              title: "Question Hooks",
              desc: "Ask a question your audience relates to, pulling them into the post.",
              example: "“Struggling to grow on LinkedIn? Here’s what might be holding you back.”",
            },
            {
              number: "06",
              title: "Data/Stat-Based Hooks",
              desc: "Spark intrigue by leading with a surprising statistic or fact.",
              example: "“85% of jobs are filled through networking — not applications.”",
            },
            {
              number: "07",
              title: "List Hooks",
              desc: "Use numbers to structure the content and set expectations.",
              example: "“3 lessons I learned the hard way as a first-time founder.”",
            },
          ].map((item, idx) => (
            <div className="hookgen-type-card" key={idx}>
              <div className="hookgen-type-num">{item.number}</div>
              <div className="hookgen-type-content">
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
                <p>
                  <strong>Example:</strong> {item.example}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Examples Section */}
      <div className="hookgen-example-section">
        <h2 className="hookgen-example-section-title">
          Examples of <span className="blue-text">LinkedIn Hooks</span>
        </h2>

        <div className="hookgen-example">
          <h4 className="hookgen-example-number">
            1. Lara Acosta: “I’ve lost at least $30,000 this year.”
          </h4>
          <div className="hookgen-image-container">
            <img
              src={require("../../assets/HookGenerator/Lara.png")}
              alt="Lara Acosta"
              className="hookgen-image"
            />
          </div>
          <p>
            “I’ve lost at least $30,000 this year” — this is a powerful LinkedIn hook because it grabs attention
            through vulnerability and shock. It immediately sparks curiosity: how did the loss happen, and what
            came after? This kind of personal statement can cut into a feed filled with success stories, making it
            more likely that someone will stop scrolling and click “See More.” It’s a classic example of an
            open-loop hook combined with shock value, designed to pull readers into a story that delivers insight,
            growth, or a hard-earned lesson—exactly the kind of content that resonates on LinkedIn.
          </p>
        </div>

        <div className="hookgen-example">
          <h4 className="hookgen-example-number">
            2. Anna York: “The #1 SEO Mistake I See in 2025.”
          </h4>
          <div className="hookgen-image-container">
            <img
              src={require("../../assets/HookGenerator/Anna.png")}
              alt="Anna York"
              className="hookgen-image"
            />
          </div>
          <p>
            This hook is effective because it combines authority, urgency, and a contrarian insight in a single
            line. By stating “The #1 SEO Mistake I See in 2025,” the author positions herself as an expert offering
            forward-looking advice, which instantly builds credibility. The follow-up—“Assuming Google is the only
            place people find you?”—challenges a widely held belief, sparking curiosity and motivating
            professionals to rethink their digital strategies. It’s a great example of a thought-leadership hook
            that provokes interest by breaking assumptions and hinting at untapped opportunities.
          </p>
        </div>

        <div className="hookgen-example">
          <h4 className="hookgen-example-number">
            3. Matt Barker: “Gimme 3 secs. I’ll show you how to start your next post.”
          </h4>
          <div className="hookgen-image-container">
            <img
              src={require("../../assets/HookGenerator/Matt.png")}
              alt="Matt Barker"
              className="hookgen-image"
            />
          </div>
          <p>
            This hook works well because it’s quick, casual, and immediately value-driven. The phrase “Gimme 3
            secs” lowers the barrier to entry, making it feel effortless to read. It uses a conversational tone
            that feels personal and informal, which is great for engagement. The promise—“I’ll show you how to
            start your next post”—directly addresses a specific pain point for LinkedIn creators struggling with
            how to begin. This is a classic example of a value-first hook that combines urgency, clarity, and
            usefulness to capture attention fast.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HookGenerator;
